import ActionCable from "actioncable";
import { getConfig, toObject, isNull, HttpHelper } from "app/helpers";
import { Track } from "components/common/track";

// class Logger {
//   public log(...messages: string[]) {
//     Track.log(messages.slice(0, -1).join(" "));
//     // console.log(messages);
//   }
// }
//
// ActionCable.logger = new Logger();
// ActionCable.startDebugging();

/**
 * Protocols that are supported by the Channel.
 */
export enum ChannelProtocol {
  Http,
  WebSocket
}

const CHANNEL_MAX_RETRY_ATTEMPTS = 3;
const CHANNEL_RETRY_BASE_DELAY_MS = 500;
const CHANNEL_RETRY_MAX_DELAY_MS = 3000;

// How long we give the WebSocket before giving up on it and falling back to
// HTTP for the rest of the session (see the constructor below).
//
// This is NOT just the handshake. `protocol` only flips to WebSocket once
// `confirm_subscription` arrives (the `connected` callback) or a broadcast
// reaches the subscription's `received` callback -- the connection-level
// `welcome` frame does neither. So everything below has to fit inside this
// window:
//
//   TLS + WS upgrade -> welcome -> subscribe -> server runs SearchChannel
//   #subscribed -> confirm_subscription back
//
// At 250ms that is unachievable against a real (non-localhost) server. The qa
// engine log showed it precisely: "Successfully upgraded to WebSocket",
// "Registered connection", then "Finished [WebSocket]" immediately after --
// the client's own timer called .disconnect() before the subscription could
// ever be confirmed, and every session silently ran over HTTP. It only ever
// worked locally, where the first broadcast lands in ~150ms.
//
// 3000ms is a deliberately generous value for MEASURING what the real
// confirm_subscription latency is on a deployed environment. It is not a
// production setting: where the WebSocket genuinely cannot connect (a proxy
// that refuses the upgrade), this is exactly how long the HTTP fallback is
// delayed. Once we have the real number, either set something realistic
// (~800-1000ms) or -- better -- stop letting a timer kill the socket: start
// HTTP immediately and switch over if the WebSocket does confirm later.
const WEBSOCKET_CONNECT_TIMEOUT_MS = 3000;

interface IPacketTrackerHash {
  [action: string]: number[];
}

export class ChannelPacketLimiter {
  public enabled: boolean = true;

  private list: IPacketTrackerHash = {};
  private paketDuration: number;
  private paketLimit: number;

  /**
   * Initialize a new ChannelPacketLimiter.
   * @param paketDuration milliseconds allowed between the limit.
   * @param paketLimit amount of pakets we allow per paketDuration.
   */
  constructor(paketDuration = 1000, paketLimit = 5) {
    this.paketDuration = paketDuration;
    this.paketLimit = paketLimit;
  }

  /**
   * Reset the limiter.
   */
  public reset(): void {
    this.list = {};
  }

  /**
   * Reset the limiter index.
   * @param index The index name.
   */
  public resetIndex(index: string): void {
    this.list[index] = [];
  }

  /**
   * Check if we are allowed to handle the response or not.
   * @param action paket action type.
   * @param timestamp timestamp of the call.
   */
  public allow(action: string, timestamp: number = Date.now()): boolean {
    if (this.enabled) {
      this.addResponse(action, timestamp);
      return this.checkLimit(action);
    } else {
      return true;
    }
  }

  private checkLimit(action: string): boolean {
    if (this.list[action].length > this.paketLimit) {
      this.flush(action);
    }
    return (this.list[action].length <= this.paketLimit);
  }

  private addResponse(action: string, timestamp: number): void {
    if (isNull(this.list[action])) {
      this.list[action] = [];
    }
    this.list[action].push(timestamp);
  }

  private flush(action: string): void {
    const timestampNow: number = Date.now() - this.paketDuration;
    while (this.list[action].length > 0) {
      const firstElem: number = this.list[action][0];
      if (firstElem < timestampNow || isNull(firstElem)) {
        this.list[action].pop();
      } else {
        break;
      }
    }
  }
}

/**
 * An object oriented timer.
 */
export class ChannelTimer {
  /** Called when the timer stops. */
  public onStop: () => void;

  private timeout: number;
  private timerHandler: TimerHandler;
  private timerHandlerArgs: any[];
  private timerHandleId: number;

  /**
   * Initialize a new Channel Timer.
   * @param timeoutSeconds in seconds
   */
  constructor(handle: TimerHandler, timeoutSeconds: number, ...args: any[]) {
    this.timerHandler = handle;
    this.timerHandlerArgs = args;
    this.setTimeout(timeoutSeconds);
  }

  /**
   * Set the timeout in seconds.
   * @param timeoutSeconds in seconds
   */
  public setTimeout(timeoutSeconds: number): void {
    this.timeout = timeoutSeconds * 1000;
    this.restart();
  }

  /**
   * Stop the timer.
   *
   * Clears timerHandleId as well as the timeout. Without that, a timer that
   * had already fired on its own still looked "running" -- only stop() ever
   * cleared the handle -- so a later stop() would run onStop a second time.
   * Concretely: the 30s status timer fires its "server timeout" alert, the
   * search then completes anyway, and stop() on "ready" re-enters onStop and
   * arms the itineraries timeout on top of an alert the user was already
   * shown.
   */
  public stop(): void {
    if (this.timerHandleId) {
      clearTimeout(this.timerHandleId);
      this.timerHandleId = 0;
      if (this.onStop) {
        this.onStop();
      }
    }
  }

  /**
   * Restart the timer.
   */
  public restart(): void {
    if (this.timerHandleId) {
      clearTimeout(this.timerHandleId);
    }

    // Wrapped so the handle is cleared when the timer fires naturally too, not
    // only via stop() -- see the note there. The handler is still invoked with
    // timerHandlerArgs as a single argument, exactly as the previous
    // `setTimeout(handler, timeout, args)` form did.
    //
    // window.setTimeout rather than the bare global: with an arrow function as
    // the callback TypeScript resolves the bare call to Node's overload, which
    // returns NodeJS.Timeout and does not fit timerHandleId's `number` type
    // (TS2322). The DOM overload on window returns number, which is what this
    // actually is in a browser.
    this.timerHandleId = window.setTimeout(() => {
      this.timerHandleId = 0;
      (this.timerHandler as any)(this.timerHandlerArgs);
    }, this.timeout);
  }
}

/**
 *
 */
export class ChannelExecuteDelay {
  private context: any;
  private action: any;
  private delay: number;
  private handler: any = null;

  constructor(context: any, delay: number = 500) {
    this.context = context;
    this.delay = delay;
  }

  public reset(): void {
    if (this.handler) {
      clearTimeout(this.handler);
    }
    this.handler = null;
    this.action = null;
  }

  public run(action: () => void): void {
    this.action = action;

    if (this.handler === null) {
      const self = this;
      this.handler = setTimeout(function () {
        if (self.action) {
          self.action.bind(self.context)();
        }
        self.reset();
      }, this.delay);
    }
  }
}

/**
 * Base class for a network Channel, designed to target ActionCable (WebSocket).
 * Has the possibility of falling back to using normal HTTP calls
 * if WebSocket is not supported by the client.
 */
export abstract class Channel {
  private cable: ActionCable.Cable;

  private channelName: string;
  private protocol: ChannelProtocol;
  private initialProtocol: ChannelProtocol;

  private channel: ActionCable.Channel;
  private limiter: ChannelPacketLimiter;

  /** Gets the packet limiter. */
  public getLimiter(): ChannelPacketLimiter { return this.limiter; }

  /** Gets the current protocol. */
  public getProtocol(): ChannelProtocol { return this.protocol; }

  /**
   * Initialize a new Channel.
   * @param channelName channel name to connect to.
   * @param protocol (optional) Set to use a specific protocol.
   */
  constructor(channelName: string, options: any = null, protocol: ChannelProtocol = null) {
    this.channelName = channelName;
    this.limiter = new ChannelPacketLimiter();
    this.protocol = ChannelProtocol.Http; // assumed until the WebSocket handshake proves otherwise
    this.initialProtocol = ChannelProtocol.WebSocket; // what we're attempting to use

    // Start connecting over WebSocket.
    this.connectWebSocket(channelName, options);

    // If we don't have a WebSocket connection within WEBSOCKET_CONNECT_TIMEOUT_MS,
    // stop waiting on it and fall back to HTTP for the rest of this session --
    // avoids the very first status request being stuck behind a slow/blocked
    // WS handshake (flaky network, a proxy or firewall that won't upgrade the
    // connection, or -- as observed on staging -- the browser simply queueing
    // the WS request behind other page-load traffic).
    setTimeout(() => {
      if (this.protocol === ChannelProtocol.Http) {
        if (this.cable) {
          // Fully disconnect the ActionCable consumer here instead of just
          // closing the raw connection. `connection.close()` (the previous
          // code here) defaults to `{ allowReconnect: true }`, so ActionCable's
          // own ConnectionMonitor keeps retrying the WebSocket handshake in
          // the background for the rest of the page's lifetime even after
          // we've committed to HTTP -- visible in DevTools as extra "zombie"
          // WebSocket connections per search, and costly server-side because
          // SearchChannel#subscribed reruns the full start_search/ready_search
          // cycle on every one of those reconnects. `.disconnect()` calls
          // `connection.close({ allowReconnect: false })` under the hood,
          // which stops the monitor for good once we've fallen back to HTTP.
          // Same @types/rails-actioncable gap as `.connection` in perform()
          // below -- `disconnect` is real at runtime but not declared on the
          // Cable type, so cast to any at the point of use.
          (this.cable as any).disconnect();
        }
        this.onConnected(this.protocol);
      }
    }, WEBSOCKET_CONNECT_TIMEOUT_MS);
  }

  /** Gets the action cable query url. */
  protected abstract getCableUrlQuery(): string;

  /**
   * Perform a request to the channel.
   * @param action channel action.
   * @param payload
   */
  protected perform(action: string, payload: any = {}, attempts: number = 0) {
    // Send over the live WebSocket connection when we actually have one.
    // (The previous version of this check -- `protocol === WebSocket &&
    // cable.connection.disconnected` -- called this exact branch only when
    // DISCONNECTED, i.e. backwards; that looks like the real reason this
    // path never worked reliably. Every other case, including "we think
    // we're on WebSocket but it just dropped", now falls through to the
    // HTTP branch below, which already has its own retry/backoff.)
    // Same @types/rails-actioncable gap as in the constructor above -- `connection`
    // is real at runtime, just not declared on the Cable type.
    if (this.protocol === ChannelProtocol.WebSocket && this.channel && !(this.cable as any).connection.disconnected) {
      this.channel.perform(action, payload);
      return;
    }

    const cableUrl: string = getConfig("action-cable-http-url");
    const url: string = `${cableUrl}/${this.channelName}/${action}?${this.getCableUrlQuery()}`;
    HttpHelper.post(url, payload)
      .then((response: any) => {
        this.received(response.data);
      }).catch((error: any) => {
        if (error.response) {
          Track.log(`[Channel] Error: ${error} - ${error.response.status} (attempt: ${attempts})`, error.response.data);
        } else {
          Track.log(`[Channel] Error: ${error} (attempt: ${attempts})`);
        }

        if (attempts < CHANNEL_MAX_RETRY_ATTEMPTS) {
          const delay = Math.min(CHANNEL_RETRY_BASE_DELAY_MS * Math.pow(2, attempts), CHANNEL_RETRY_MAX_DELAY_MS);
          setTimeout(() => this.perform(action, payload, attempts + 1), delay);
        } else {
          // Previously this just logged and silently gave up -- the caller
          // (and the user) never found out that this action never happened.
          // Let subclasses decide how to surface that (e.g. an error popup).
          Track.log(`[Channel] Giving up on ${action} after ${attempts + 1} attempts`);
          this.onActionFailed(action);
        }
      });
  }

  /**
   * Called when an action permanently failed (HTTP retries exhausted, or the
   * server reported an error for it over WebSocket). Default is a no-op;
   * override to notify the user. See SearchChannel for an example.
   * @param action the channel action that failed.
   */
  protected onActionFailed(action: string): void {
    // Intentionally empty by default.
  }

  /**
   * Called when we connect to a channel or switched protocol.
   * @param protocol the protocol we are currently running.
   */
  protected abstract onConnected(protocol: ChannelProtocol): void;

  /**
   * Called when we disconnect from a channel.
   */
  protected abstract onDisconnected(): void;

  /**
   * Called when we receive something new from the channel.
   * @param response the response body we get from the channel.
   * @returns true, if the packet limiter should count; otherwise, false.
   */
  protected abstract onReceived(response: any): boolean;

  private connectWebSocket(channelName: string, options: any = null): void {
    // NOTE: the original (abandoned 2019) version of this read
    // getConfig("url") here, but the meta tag Rails' own action_cable_meta_tag
    // helper renders is named "action-cable-url" (see
    // app/views/layouts/*.html.erb: `<%= action_cable_meta_tag %>`) -- so the
    // old code was reading a meta tag that doesn't exist, which alone would
    // have made every WebSocket connection attempt fail silently.
    const cableUrl = `${getConfig("action-cable-url")}?${this.getCableUrlQuery()}`;
    this.cable = ActionCable.createConsumer(cableUrl);

    let parameters: any = {
      channel: channelName
    };

    if (options) {
      parameters = Object.assign(parameters, options);
    }

    this.channel = this.cable.subscriptions.create(parameters, {
      connected: this.webSocketConnected.bind(this),
      disconnected: this.disconnected.bind(this),
      received: this.webSocketReceived.bind(this)
    });
  }

  private webSocketConnected(): void {
    if (this.protocol === ChannelProtocol.Http) {
      Track.log(`[Channel] Use WebSocket protocol`);
      this.protocol = ChannelProtocol.WebSocket;
    }

    this.onConnected(this.protocol);
  }

  private webSocketReceived(response: any): void {
    if (this.protocol === ChannelProtocol.Http) {
      this.protocol = ChannelProtocol.WebSocket;
    }
    this.received(response);
  }

  private disconnected(): void {
    if (this.initialProtocol === ChannelProtocol.WebSocket &&
        this.protocol === ChannelProtocol.WebSocket) {
      this.protocol = ChannelProtocol.Http;
      Track.log(`[Channel] Use Http protocol`);
      this.perform("status");
    }

    this.onDisconnected();
  }

  private received(response: any): void {
    const obj: any = toObject(response);
    if (obj.action) {
      // console.log(`[Channel] Response: ${obj.action}`);

      if (this.limiter.allow(obj.action)) {
        if (!this.onReceived(obj)) {
          this.limiter.resetIndex(obj.action);
        }
      } else {
        // throw new Error("Packet is ignored.");
      }
    } else {
      throw new Error("Packet type is not supported.");
    }
  }
}
